import Vue from 'vue';
import Component from 'vue-class-component';
import Moment from 'moment';
import { Emit, Mixins, Prop } from 'vue-property-decorator';
import { AB_DAYS, WEEK_DAYS } from '@/values/general.values';
import { Placeholder, ScheduleEntryCalendar, ScheduleResponseDto } from '@/tools/models/class-builder.models';
import { ScheduleEntrySelected } from '@/tools/params/class-builder.params';
import { PlaceholderService } from '@/tools/services/placeholder.service';
import { ClassBuilderCalendarMixin } from '../class-builder-calendar.mixin';
import { CalendarEventDetail } from '@webcat/commons-web-components/dist/calendar/models/calendar-event-detail';
import EdClassBuilderPlaceholderModal from '../ed-class-builder-placeholders-modal/ed-class-builder-placeholders-modal.component.vue';
import { lightOrDarkFromColor } from '@/utils/color-utils';
import { EdContextMenuModel } from '@/plugins/ed-context-menu/ed-context-menu.plugin';
import { mouseEnter } from '@/constants/constants-components';
import { EdGenericCalendarWeekdays } from '@/core/components/ed-generic-calendar/interfaces/ed-generic-calendar-weekdays.interface';

@Component({
  name: 'ed-class-builder-schedule',
  components: {
    EdClassBuilderBlock: () => import(/* EdClassBuilderBlock */ '../ed-class-builder-block/ed-class-builder-block.component.vue'),
    EdGenericCalendar: () => import(/* EdGenericCalendar */ '@/core/components/ed-generic-calendar/ed-generic-calendar.component.vue'),
  }
})
export default class EdClassBuilderScheduleComponent extends Mixins(ClassBuilderCalendarMixin) {
  @Prop({ default: () => [] }) readonly scheduleEntries: ScheduleResponseDto[];
  @Prop({ required: true }) readonly isAbleToEdit: boolean;
  @Prop({ default: () => [0, 1, 2, 3, 4, 5, 6] }) readonly weekdays: number[] | EdGenericCalendarWeekdays[];

  @Emit('onToggleSelectBlock')
  emitOnToggleSelectBlock(data: ScheduleEntrySelected): ScheduleEntrySelected {
    return data;
  }

  @Emit('onGoToAddPlaceholders')
  emitOnGoToAddPlaceholders(data: ScheduleEntrySelected): ScheduleEntrySelected {
    return data;
  }

  @Emit('onRefetchSchedule')
  emitOnRefetchSchedule(): void {}

  get events(): CalendarEventDetail[] {
    return this._mapToCalendarEvent(this.calendarEvents);
  }

  get calendarEvents(): ScheduleEntryCalendar[] {
    return this.getCalendarEventsFormat(this.scheduleEntries);
  }

  getCalendarEventsFormat(scheduleClass: ScheduleResponseDto[]): ScheduleEntryCalendar[] {
    const events: ScheduleEntryCalendar[] = [];

    scheduleClass.forEach((scheduleDay) => {
      const listDays = this.ScheduleProvider.isWeeklySchedule ? WEEK_DAYS : AB_DAYS;
      const dayNumber = listDays.indexOf(scheduleDay.workingDay.toLocaleLowerCase());
      const date = Moment().weekday(dayNumber).format('YYYY-MM-DD');
      
      scheduleDay.entries.forEach((entry) => {
        const name = entry.scheduleBlock.title;

        events.push(Object.assign(new ScheduleEntryCalendar(), {
          id: entry.id,
          name,
          start: `${date} ${entry.startTime}`,
          end: `${date} ${entry.endTime}`,
          placeholders: entry.placeholders,
          workingDay: scheduleDay.workingDay,
          scheduleBlockId: entry.scheduleBlock.id,
        }));
      });
    });

    return events;
  } 

  resetSelection() {
    this.calendarEvents.forEach((event) => {
      const element: any = this.$refs[`block${event.id}`];

      if (element) {
        element.resetSelect();
      }
    });
  }

  async unrelatePlaceholder({ entryId, placeholderId }: { entryId: number, placeholderId: number}) {
    const classId = this.$route.params.classId;
    await PlaceholderService.unrelatePlaceholder(classId, [entryId], [placeholderId]);
    this.emitOnRefetchSchedule();
  }

  private _mapToCalendarEvent(scheduleEntries: ScheduleEntryCalendar[]): CalendarEventDetail[] {
    const result = scheduleEntries.map(entry => ({
      uid: `${entry.id}`,
      startDate: entry.start,
      endDate: entry.end,
      summary: entry.name,
      allDay: false,
      custom: {
        events: [
          { 
            target: '.see-more', 
            handler: (_) => this._seeMoreOnClick(entry)
          },
          { 
            target: '.plus-button', 
            handler: (_) => this._plussButtonOnClick(entry)
          },
          { 
            target: `.label-${entry.id}`, 
            type: mouseEnter,
            handler: (params) => this._mousePlaceholderHandler(params, entry.name)
          },
          ...((entry.placeholders || []).map((placeholder: any) => ({
            target: `.unrelate-placeholder-${placeholder.placeholder.id}`,
            handler: (_) => this._unrelateOnClick(entry, placeholder.placeholder)
          }))),
          ...((entry.placeholders || []).map((placeholder: any) => ({
            target: `.title-placeholder-${placeholder.placeholder.id}`,
            type: mouseEnter,
            handler: (params) => this._mousePlaceholderHandler(params, placeholder.placeholder.title)
          })))
        ]
      }
    }));
    return result;
  }


  private _seeMoreOnClick(event: ScheduleEntryCalendar) {
    this.$EdModal.createModal(EdClassBuilderPlaceholderModal, {
      isAbleToEdit: this.isAbleToEdit,
      placeholders: event.placeholders,
      lightOrDark: this.lightOrDark,
      emitUnrelatePlaceholder: (placeholderId: number) => this.unrelatePlaceholder({
        entryId: event.id,
        placeholderId,
      })
    })
  }

  private _plussButtonOnClick(event: ScheduleEntryCalendar) {
    this.emitOnGoToAddPlaceholders({
      id: event.id,
      workingDay: event.workingDay,
      placeholders: event.placeholders.map((placeholder: any) => placeholder.placeholder),
      scheduleBlockId: event.scheduleBlockId,
    });
  }

  private _unrelateOnClick(event: ScheduleEntryCalendar, placeholder: Placeholder) {
    this.unrelatePlaceholder({entryId: event.id, placeholderId: placeholder.id});
  }

  private _mousePlaceholderHandler(params: any, title: String) {
    const type = params.type;
    if (type === mouseEnter) {
      const mouseEvent: MouseEvent = params.mouseEvent;
      if (mouseEvent) {
        this.$EdContextMenu.show(new EdContextMenuModel({
          position: { x: mouseEvent.x, y: mouseEvent.y },
          options: [{ text: title }],
          darkMode: true
        }));
      }
    } else {
      this.$EdContextMenu.close();
    }
  }

  lightOrDark(color) {
    return lightOrDarkFromColor(color);
  }
}